import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { isEmpty, trim } from "lodash";
import { DataContext } from "../../../context/DataContext";
import {
  deleteToken,
  deleteUserData,
  getToken,
  getUser,
  getUserByUsername,
  updateMyAccountAsync,
} from "../../../api/Account";
import { EstadoError, useAviso } from "../../../components/base";

// Texto solo para lectores de pantalla.
const ocultoVisual = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
};

const UpdateUserData = ({ onGuardado }) => {
  const { setIsLoading, reload, setReload, setIsLogged } =
    useContext(DataContext);
  const aviso = useAviso();
  const [firstName, setfirstName] = useState("");
  const [secondName, setSecondName] = useState("");
  const [lastName, setLastName] = useState("");
  const [secondlastName, setSecondLastName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});

  const user = getUser();
  const token = getToken();

  const cargarDatos = async () => {
    setCargando(true);
    setErrorCarga(false);
    const result = await getUserByUsername(token);
    if (!result.statusResponse) {
      if (result.error === "eX01") {
        setIsLoading(false);
        deleteUserData();
        deleteToken();
        setIsLogged(false);
        return;
      }
      setIsLoading(false);
      setCargando(false);
      setErrorCarga(true);
      aviso.error(result.error || "No se pudieron cargar tus datos");
      return;
    }

    setfirstName(result.data.firstName ?? "");
    setSecondName(result.data.secondName ?? "");
    setPhone(result.data.phone ?? "");

    setLastName(result.data.lastName ?? "");
    setSecondLastName(result.data.secondLastName ?? "");
    setAddress(result.data.address ?? "");
    setCargando(false);
  };

  useEffect(() => {
    setIsLoading(true);
    if (token === null || user === null) {
      setIsLogged(false);
      setIsLoading(false);
      return;
    } else {
      cargarDatos();
      setIsLoading(false);
    }
  }, []);

  // El objeto que se envía al servidor: es el mismo de siempre.
  const setData = () => {
    const data = {
      firstName: trim(firstName),
      secondName: trim(secondName),
      lastName: trim(lastName),
      secondlastName: trim(secondlastName),
      phone: trim(phone),
      address: trim(address),
    };
    return data;
  };

  // Una sola llamada a la API con todos los campos. Devuelve la respuesta si guardó, o null.
  const execute = async () => {
    setIsLoading(true);
    const data = setData();
    const result = await updateMyAccountAsync(token, data);
    if (!result.statusResponse) {
      setIsLoading(false);
      if (result.error === "eX01") {
        deleteUserData();
        deleteToken();
        setIsLogged(false);
        return null;
      }

      aviso.error(result.error || "No se pudieron guardar los datos");
      return null;
    }
    setReload(!reload);
    setIsLoading(false);
    return result;
  };

  const guardar = async (evento) => {
    evento.preventDefault();
    if (guardando) return;

    const faltantes = {};
    if (isEmpty(trim(firstName))) faltantes.firstName = "Ingrese el primer nombre";
    if (isEmpty(trim(address))) faltantes.address = "Ingrese la dirección";
    setErrores(faltantes);
    if (!isEmpty(faltantes)) return;

    setGuardando(true);
    const result = await execute();
    setGuardando(false);
    if (result) {
      aviso.exito("Datos actualizados");
      if (onGuardado) onGuardado(result.data);
    }
  };

  // Escribir en un campo limpia su error.
  const cambiar = (campo, poner) => (e) => {
    poner(e.target.value.toUpperCase());
    if (errores[campo]) setErrores({ ...errores, [campo]: undefined });
  };

  const campo = (clave, etiqueta, valor, poner, extra) => (
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        variant="outlined"
        onChange={cambiar(clave, poner)}
        value={valor}
        label={etiqueta}
        error={Boolean(errores[clave])}
        helperText={errores[clave]}
        disabled={guardando}
        {...extra}
      />
    </Grid>
  );

  return (
    <div>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography
          component="h2"
          variant="h6"
          sx={{ fontWeight: 700, color: "primary.main", mb: 2 }}
        >
          Editar datos
        </Typography>

        {errorCarga ? (
          <EstadoError
            titulo="No se pudieron cargar tus datos"
            onReintentar={cargarDatos}
          />
        ) : cargando ? (
          <Box role="status" aria-live="polite">
            <Box component="span" sx={ocultoVisual}>
              Cargando…
            </Box>
            <Grid container spacing={2} aria-hidden="true">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Skeleton
                    variant="rounded"
                    height={56}
                    sx={{
                      borderRadius: 2.5,
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box component="form" onSubmit={guardar} noValidate>
            <Grid container spacing={2}>
              {campo("firstName", "Primer nombre", firstName, setfirstName, {
                required: true,
              })}
              {campo("secondName", "Segundo nombre", secondName, setSecondName)}
              {campo("lastName", "Primer apellido", lastName, setLastName)}
              {campo(
                "secondlastName",
                "Segundo apellido",
                secondlastName,
                setSecondLastName
              )}
              {campo("phone", "Teléfono", phone, setPhone, {
                inputProps: { inputMode: "tel" },
              })}
              {campo("address", "Dirección", address, setAddress, {
                required: true,
              })}
            </Grid>

            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: { xs: "stretch", sm: "flex-end" },
              }}
            >
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={guardando}
                startIcon={
                  guardando ? <CircularProgress size={18} color="inherit" /> : null
                }
                sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 200 }}
              >
                Guardar cambios
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </div>
  );
};

export default UpdateUserData;
